import React, { useState } from "react";
import { indexWrapCss, hideScrollbarCss } from "./css/Author.style"
import Header from "src/common/layouts/header/Header"
import Footer from "src/common/layouts/footer/Footer";
import AuthorDetailPage from "src/domain/author/authorDetail/AuthorDetailPage";
import BooksList from "src/domain/author/components/BookList";
import { useIsMobile } from "../../hooks/useIsMobile";
import AuthorDetailPageMobile from "./mobile/AuthorDetailPageMobile";
import BookListMobile from "./mobile/BookListMobile";
import AuthorNoteList from "./components/AuthorNoteList";
import AuthorNoteDetail from "./components/AuthorNoteDetail";
import AuthorNoteWrite from "./components/AuthorNoteWrite";
import {ReactComponent as Book} from 'src/assets/author/book_author.svg';
import {ReactComponent as Conversation} from 'src/assets/author/conversation_author.svg';
import {ReactComponent as Genre} from 'src/assets/author/genre_author.svg';
import { motion } from "framer-motion";


interface Author {
    authorName?: string;
    authorKeyword?: string;
    authorIntro?: string;
    authorEmail?: string;
    authorDescription?: string;
    keyword1?: string;
    mbti?: string;
    writingGenre?: string;
    inspirationSource?: string;
    happiestWritingMoment?: string;
    favoriteWorkAndQuote?: string;
    behindCreationEpisode?: string;
    futureWritingPlan?: string;
    [key: string]: any;
}

interface Book {
    bookTitle: string;
    coverImage: string;
}

interface AuthorPageProps {
    authors: Author;
    bookData: Book[];
    onFollowToggle: () => void;
    isFollowing: boolean;
    followAlert: boolean;
    setFollowAlert: (value: boolean) => void;
}

interface QAItem {
    question: string;
    answer: string;
}

type NoteView = { mode: 'list' } | { mode: 'detail'; noteId: string } | { mode: 'write'; editData?: any };

const AuthorPage = ({ authors, bookData, onFollowToggle, isFollowing, followAlert, setFollowAlert }: AuthorPageProps) => {
    const isMobile = useIsMobile();
    const [search, setSearch] = useState<any>([]);
    const [noteView, setNoteView] = useState<NoteView>({ mode: 'list' });

    const onSearchHandler = (e: any) => {
        setSearch(e.target.value);
    };

    const authorEmail = authors.authorEmail || '';
    const authorStatus: string | null = localStorage.getItem("authorStatus");
    const loggedInEmail: string | null = localStorage.getItem("email");
    const isOwner = !!authorStatus && loggedInEmail === authorEmail;

    const qaItems: QAItem[] = [
        { question: "소설가로 데뷔하시게 된 계기가 무엇인가요?", answer: authors.authorDescription || "" },
        { question: "가장 애착이 가는 작품과 그 이유는?", answer: authors.favoriteWorkAndQuote || "" },
        { question: "독자에게 소개하고 싶은 구절이 있다면?", answer: authors.behindCreationEpisode || "" },
        { question: "글을 쓸 때 가장 중요하게 생각하는 점은?", answer: authors.inspirationSource || "" },
        { question: "앞으로 어떤 이야기를 쓰고 싶으신가요?", answer: authors.futureWritingPlan || "" },
        { question: "작가로서 가장 행복한 순간은 언제인가요?", answer: authors.happiestWritingMoment || "" },
    ].filter(item => item.answer.trim() !== "");

    /* ── 작가 노트 섹션 (PC / 모바일 공용) ── */
    const NoteSection = () => (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
        >
            {/* 헤더 */}
            <div className="flex items-center gap-3 mb-6">
                <span className="text-[20px] font-bold text-[#111827]">작가 노트</span>
            </div>

            {/* 노트 컨테이너 */}
            <div className="bg-[rgba(16,185,129,0.1)] rounded-[24px] p-8 md:p-5">
                <motion.div
                    key={noteView.mode}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                >
                    {noteView.mode === 'list' && (
                        <AuthorNoteList
                            authorEmail={authorEmail}
                            isOwner={false}
                            onViewDetail={(noteId) => setNoteView({ mode: 'detail', noteId })}
                            onWrite={() => {}}
                        />
                    )}
                    {noteView.mode === 'detail' && (
                        <AuthorNoteDetail
                            noteId={(noteView as any).noteId}
                            isOwner={false}
                            onBack={() => setNoteView({ mode: 'list' })}
                            onEdit={() => {}}
                        />
                    )}
                </motion.div>
            </div>
        </motion.div>
    );

    return (
        <div css={indexWrapCss}>
            <Header
                onChange={onSearchHandler}
                searchValue={search}
                name="search"
                activeFlag=""
            />

            {isMobile ? (
                <>
                    <AuthorDetailPageMobile
                        authors={authors}
                        isFollowing={isFollowing}
                        onFollowToggle={onFollowToggle}
                        followAlert={followAlert}
                        setFollowAlert={setFollowAlert}
                    />

                    {/* 흰 카드 - 히어로 아래 자연스럽게 연결 */}
                    <div className="bg-white rounded-t-[32px] -mt-6 relative z-10 flex flex-col pb-12">

                        {/* 상단 핸들 바 */}
                        <div className="flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1 rounded-full bg-[#e0e0e0]" />
                        </div>

                        {/* keyword 섹션 */}
                        {authors.keyword1 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }} transition={{ duration: 0.5 }}
                                className="mx-4 mt-5"
                            >
                                <div className="flex flex-col items-center text-center gap-1 py-3">
                                    <div className="flex items-center gap-1.5 text-[#888] text-[13px]">
                                        <Genre />
                                        <span>{authors.authorName} 작가님은</span>
                                    </div>
                                    <p className="text-[#101828] text-[17px] font-bold leading-snug">
                                        <span className="text-[#19cf7a]">{authors.keyword1}</span>{' '}
                                        글을 쓰고 있어요!
                                    </p>
                                </div>
                            </motion.div>
                        )}

                        {/* Stats 카드 */}
                        {bookData.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 }}
                                className="mx-4 mt-4 bg-white border border-[#f3f4f6] rounded-2xl shadow-[0px_4px_24px_rgba(25,207,122,0.12)] px-4 py-4"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-[rgba(25,207,122,0.1)] rounded-2xl flex items-center justify-center">
                                            <Book />
                                        </div>
                                        <span className="text-[#364153] text-[14px] font-medium">출간작 개수</span>
                                    </div>
                                    <span className="text-[#101828] text-[20px] font-extrabold">{bookData.length}</span>
                                </div>
                            </motion.div>
                        )}

                        <div className="h-2 bg-[#f9fafb] mt-5 mx-0" />

                        {/* 미니 인터뷰 Q&A */}
                        {qaItems.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }} transition={{ duration: 0.5 }}
                                className="mx-4 mt-5 flex flex-col gap-3"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Conversation />
                                    <span className="text-[#101828] text-[18px] font-bold">작가와의 대화</span>
                                </div>
                                {qaItems.map((item, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: i * 0.07 }}
                                        className="bg-[rgba(25,207,122,0.03)] border border-[rgba(25,207,122,0.1)] rounded-2xl px-5 py-5 flex flex-col gap-3"
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="text-[#19cf7a] text-[20px] font-bold leading-none flex-shrink-0 mt-0.5">Q</span>
                                            <p className="text-[#101828] text-[15px] font-semibold leading-snug">{item.question}</p>
                                        </div>
                                        <div className="h-px bg-[rgba(25,207,122,0.12)]" />
                                        <div className="flex items-start gap-3">
                                            <span className="text-[rgba(0,200,150,0.5)] text-[20px] font-bold leading-none flex-shrink-0 mt-0.5">A</span>
                                            <p className="text-[14px] text-[#4a5565] leading-relaxed whitespace-pre-wrap">{item.answer}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}

                        <div className="h-2 bg-[#f9fafb] mt-5" />

                        {/* 모든 작품 */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }} transition={{ duration: 0.5 }}
                            className="mt-5"
                        >
                            <BookListMobile bookData={bookData} />
                        </motion.div>

                        <div className="h-2 bg-[#f9fafb] mt-5" />

                        {/* 작가 노트 */}
                        <div className="mx-4 mt-5">
                            <NoteSection />
                        </div>
                    </div>
                </>
            ) : (
                /* PC: 좌측 고정 + 전체 화면 스크롤 */
                <div className="flex bg-[#F9FAFB] min-h-[calc(100vh-100px)]">
                    <div className="max-w-[1200px] w-full mx-auto px-6 flex flex-row gap-8 relative">

                        {/* 좌측: sticky 프로필 */}
                        <div css={hideScrollbarCss} className="w-[300px] flex-shrink-0 py-12 flex flex-col gap-3 sticky top-[20px] max-h-[calc(100vh-40px)] overflow-y-auto self-start z-10">
                            <AuthorDetailPage
                                authors={authors}
                                isFollowing={isFollowing}
                                onFollowToggle={onFollowToggle}
                                followAlert={followAlert}
                                setFollowAlert={setFollowAlert}
                                bookCount={bookData.length}
                            />
                        </div>

                        {/* 우측: 소개 → 작품 → 작가노트 한 번에 스크롤 */}
                        <div className="flex-1 min-w-0 flex flex-col py-12 gap-10">

                            {/* Keyword banner */}
                            {authors.keyword1 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }}
                                    className="flex items-center border-[1px] border-[#19CF7A33] gap-4 bg-gradient-to-r from-[rgba(25,207,122,0.1)] via-[rgba(25,207,122,0.02)] to-transparent rounded-3xl px-8 py-5">
                                    <div className="w-12 h-12 bg-white rounded-full shadow flex items-center justify-center flex-shrink-0">
                                        <Genre />
                                    </div>
                                    <span className="font-bold text-[#101828] text-2xl">
                                        {authors.authorName} 작가님은{" "}
                                        <span className="text-[#19cf7a]">{authors.keyword1}</span>
                                        {" "}글을 쓰고 있어요!
                                    </span>
                                </motion.div>
                            )}

                            {/* 미니 인터뷰 */}
                            {qaItems.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }}
                                    className="flex flex-col gap-5">
                                    <div className="flex items-center gap-3">
                                        <Conversation />
                                        <span className="text-2xl font-bold text-[#101828]">미니 인터뷰</span>
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        {qaItems.map((item, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6, delay: i * 0.1 }}
                                                className="bg-white border border-[#e5e7eb] rounded-2xl shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)] px-8 py-8 flex flex-col gap-[20px]">
                                                <div className="flex items-start gap-4">
                                                    <span className="font-extrabold text-[#19cf7a] text-2xl leading-none flex-shrink-0">Q</span>
                                                    <p className="font-bold text-[#101828] text-lg leading-snug">{item.question}</p>
                                                </div>
                                                <div className="h-px bg-[#f3f4f6]" />
                                                <div className="flex items-start gap-4">
                                                    <span className="font-extrabold text-[rgba(0,200,150,0.4)] text-2xl leading-none flex-shrink-0">A</span>
                                                    <p className="text-base text-[#4a5565] leading-relaxed whitespace-pre-wrap">{item.answer}</p>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* 모든 작품 */}
                            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }}>
                                <BooksList bookData={bookData} />
                            </motion.div>

                            {/* 작가 노트 */}
                            <NoteSection />
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AuthorPage;
